import { useState } from "react";
import { useIdioma } from "../../i18n/IdiomaProvider";
import styles from "./CalendarioCustomizado.module.css";

const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const MESES_CURTOS = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

type ViewCalendario = "dias" | "meses" | "anos";

interface CelulaDia {
  data: Date;
  foraMes: boolean;
}

export function parseDataBr(v?: string): Date | null {
  if (!v) return null;
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const dia = Number(m[1]);
  const mes = Number(m[2]) - 1;
  const ano = Number(m[3]);
  const d = new Date(ano, mes, dia);
  return d.getDate() === dia && d.getMonth() === mes && d.getFullYear() === ano
    ? d
    : null;
}

export function formatarDataBr(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

interface CalendarioCustomizadoProps {
  valor?: string;
  onSelecionar: (dataBr: string) => void;
}

function limparHora(data: Date) {
  return new Date(data.getFullYear(), data.getMonth(), data.getDate());
}

function calcularFeriadosBrasil(ano: number): Record<string, string> {
  const feriados: Record<string, string> = {
    "01-01": "Confraternização Universal",
    "04-21": "Tiradentes",
    "05-01": "Dia do Trabalhador",
    "09-07": "Independência do Brasil",
    "10-12": "Nossa Sra. Aparecida",
    "11-02": "Finados",
    "11-15": "Proclamação da República",
    "11-20": "Dia da Consciência Negra",
    "12-25": "Natal",
  };

  const a = ano % 19;
  const b = Math.floor(ano / 100);
  const c = ano % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const ff = Math.floor((b + 8) / 25);
  const g = Math.floor((b - ff + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mesPascoa = Math.floor((h + l - 7 * m + 114) / 31);
  const diaPascoa = ((h + l - 7 * m + 114) % 31) + 1;
  const pascoa = new Date(ano, mesPascoa - 1, diaPascoa);

  const adicionar = (data: Date, nome: string) => {
    feriados[
      `${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`
    ] = nome;
  };

  adicionar(new Date(pascoa.getTime() - 47 * 86400000), "Carnaval");
  adicionar(new Date(pascoa.getTime() - 2 * 86400000), "Paixão de Cristo");
  adicionar(new Date(pascoa.getTime() + 60 * 86400000), "Corpus Christi");

  return feriados;
}

function montarDias(ano: number, mes: number): CelulaDia[] {
  const primeiroDiaSemana = new Date(ano, mes, 1).getDay();
  const inicioGrade = new Date(ano, mes, 1 - primeiroDiaSemana);

  return Array.from({ length: 42 }, (_, indice) => {
    const data = new Date(
      inicioGrade.getFullYear(),
      inicioGrade.getMonth(),
      inicioGrade.getDate() + indice
    );

    return {
      data,
      foraMes: data.getMonth() !== mes,
    };
  });
}

function tituloCalendario(view: ViewCalendario, data: Date, traduzir: (texto: string) => string) {
  const ano = data.getFullYear();
  if (view === "dias") return `${traduzir(MESES[data.getMonth()])} ${ano}`;
  if (view === "meses") return String(ano);

  const inicio = Math.floor(ano / 12) * 12;
  return `${inicio} - ${inicio + 11}`;
}

/** Calendario personalizado com navegacao por dias, meses e anos. Nao usa input date. */
export function CalendarioCustomizado({ valor, onSelecionar }: CalendarioCustomizadoProps) {
  const { traduzir } = useIdioma();
  const selecionado = parseDataBr(valor);
  const base = selecionado ?? new Date();
  const [visivel, setVisivel] = useState(new Date(base.getFullYear(), base.getMonth(), 1));
  const [view, setView] = useState<ViewCalendario>("dias");

  const ano = visivel.getFullYear();
  const mes = visivel.getMonth();
  const hoje = limparHora(new Date());
  const celulas = montarDias(ano, mes);
  const feriados = calcularFeriadosBrasil(ano);
  const inicioBlocoAnos = Math.floor(ano / 12) * 12;
  const anos = Array.from({ length: 12 }, (_, indice) => inicioBlocoAnos + indice);

  function navegar(direcao: number) {
    if (view === "dias") {
      setVisivel(new Date(ano, mes + direcao, 1));
      return;
    }

    if (view === "meses") {
      setVisivel(new Date(ano + direcao, mes, 1));
      return;
    }

    setVisivel(new Date(ano + direcao * 12, mes, 1));
  }

  function alternarTitulo() {
    if (view === "dias") {
      setView("meses");
      return;
    }

    if (view === "meses") {
      setView("anos");
      return;
    }

    setView("dias");
  }

  function selecionarMes(indiceMes: number) {
    setVisivel(new Date(ano, indiceMes, 1));
    setView("dias");
  }

  function selecionarAno(anoSelecionado: number) {
    setVisivel(new Date(anoSelecionado, mes, 1));
    setView("meses");
  }

  return (
    <div className={styles.calendario}>
      <div className={styles.cabecalho}>
        <button
          type="button"
          className={styles.nav}
          onClick={() => navegar(-1)}
          aria-label={traduzir("Anterior")}
        >
          ‹
        </button>

        <button
          type="button"
          className={styles.mesAno}
          onClick={alternarTitulo}
          aria-label={traduzir("Alternar visualização do calendário")}
        >
          {tituloCalendario(view, visivel, traduzir)}
        </button>

        <button
          type="button"
          className={styles.nav}
          onClick={() => navegar(1)}
          aria-label={traduzir("Próximo")}
        >
          ›
        </button>
      </div>

      {view === "dias" && (
        <>
          <div className={styles.semana}>
            {DIAS_SEMANA.map((d) => (
              <span key={d}>{traduzir(d)}</span>
            ))}
          </div>

          <div className={styles.grade}>
            {celulas.map(({ data, foraMes }) => {
              const dataLimpa = limparHora(data);
              const ehSelecionado =
                selecionado != null && dataLimpa.getTime() === limparHora(selecionado).getTime();
              const ehHoje = dataLimpa.getTime() === hoje.getTime();
              const chaveFeriado = `${String(data.getMonth() + 1).padStart(2, "0")}-${String(
                data.getDate()
              ).padStart(2, "0")}`;
              const nomeFeriado = feriados[chaveFeriado] ?? "";

              return (
                <button
                  key={data.toISOString()}
                  type="button"
                  className={[
                    styles.dia,
                    foraMes ? styles.diaFora : "",
                    ehSelecionado ? styles.selecionado : "",
                    ehHoje ? styles.hoje : "",
                    nomeFeriado ? styles.feriado : "",
                    nomeFeriado ? "tooltip tooltipUp" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  data-tooltip={nomeFeriado ? traduzir(nomeFeriado) : ""}
                  onClick={() => onSelecionar(formatarDataBr(data))}
                >
                  {data.getDate()}
                </button>
              );
            })}
          </div>

          <div className={styles.rodape}>
            <button
              type="button"
              className={styles.hojeBtn}
              onClick={() => onSelecionar(formatarDataBr(new Date()))}
            >
              {traduzir("Hoje")}
            </button>
          </div>
        </>
      )}

      {view === "meses" && (
        <div className={styles.gradeMesAno}>
          {MESES_CURTOS.map((nomeMes, indice) => (
            <button
              key={nomeMes}
              type="button"
              className={`${styles.itemMesAno} ${indice === mes ? styles.itemMesAnoAtivo : ""}`}
              onClick={() => selecionarMes(indice)}
            >
              {traduzir(nomeMes)}
            </button>
          ))}
        </div>
      )}

      {view === "anos" && (
        <div className={styles.gradeMesAno}>
          {anos.map((anoItem) => (
            <button
              key={anoItem}
              type="button"
              className={`${styles.itemMesAno} ${anoItem === ano ? styles.itemMesAnoAtivo : ""}`}
              onClick={() => selecionarAno(anoItem)}
            >
              {anoItem}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
